import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  PaymentElement,
  Elements,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {
  onCreatePaymentMethod,
  onCreateSetupIntent,
} from 'redux/actions/paymentActions';
import { store } from 'react-notifications-component';
import LoadingAnimation from 'components/common/LoadingAnimation';
import { getStripePromise } from 'utils/stripe.util';
import '../../style.scss';

// PaymentElement's own `options` only accepts things like `fields`/`layout`/
// `defaultValues` - `clientSecret` belongs solely on <Elements>. Passing the
// same object with `clientSecret` to both was silently rejected by Stripe.js
// ("clientSecret is not a recognized parameter") and left the bank-account
// fields collapsed to a near-zero-height, empty iframe.
const paymentElementOptions = {
  fields: {
    billingDetails: {
      name: 'never',
      email: 'never',
    },
  },
};

const EFTCard = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const dispatch = useDispatch();
  const paymentIntent = useSelector(state => state.paymentIntent);
  const elementsOptions = {
    clientSecret: paymentIntent?.clientSecret,
  };
  useEffect(() => {
    dispatch(onCreateSetupIntent());
  }, [dispatch]);
  if (paymentIntent?.clientSecret) {
    return (
      <Elements options={elementsOptions} stripe={getStripePromise()}>
        <div className="row eft-modal">
          <div className="col-xl-12">
            <div className="labeled-group floating">
              <input
                id="Field-nameInput"
                className="form-control"
                type="text"
                name="full_name"
                required="required"
                onChange={({ target }) => setName(target.value)}
                value={name}
              />
              <label>Full Name</label>
            </div>
          </div>
          <div className="col-xl-12">
            <div className="labeled-group floating">
              <input
                id="Field-emailInput"
                className="form-control"
                type="email"
                name="email"
                required="required"
                onChange={({ target }) => setEmail(target.value)}
                value={email}
              />
              <label>Email</label>
            </div>
          </div>
        </div>
        <PaymentElement options={paymentElementOptions} />
        <AddEftCard email={email} name={name} />
      </Elements>
    );
  }
  return <LoadingAnimation />;
};

const AddEftCard = ({ email, name }) => {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    try {
      if (!stripe || !elements) {
        return;
      }
      setSubmitting(true);
      // confirmSetup() is payment-method-agnostic - unlike the old
      // confirmAcssDebitSetup(), it works for whichever bank-debit method
      // PaymentElement actually rendered for this customer (ACSS Debit for
      // Canada, ACH for the US, Bacs Debit for the UK, ...), so enabling
      // more countries on the backend's SetupIntent doesn't require another
      // frontend change here.
      const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          payment_method_data: {
            billing_details: { name, email },
          },
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (error) {
        store.addNotification({
          title: 'Card Addition Error!',
          message: error.message,
          type: 'danger',
          insert: 'top',
          container: 'top-right',
          animationIn: ['animated', 'bounceIn'],
          animationOut: ['animated', 'fadeOut'],
          dismiss: {
            duration: 8000,
          },
        });
        return;
      }
      if (setupIntent.status === 'succeeded') {
        // eslint-disable-next-line camelcase
        const { payment_method, id } = setupIntent || {};
        dispatch(
          onCreatePaymentMethod({
            paymentMethodId: payment_method,
            StripeSetupIntentId: id,
            name,
            email,
          }),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {submitting ? <LoadingAnimation /> : null}
      {/* eslint-disable-next-line react/button-has-type */}
      <button
        onClick={handleSubmit}
        disabled={submitting || !stripe}
        className="btn btn-block btn-primary"
      >
        Confirm and Continue Saving Bank Account
      </button>
    </>
  );
};
export default EFTCard;
